import { z } from 'zod'
import * as Sentry from '@sentry/react'

// Schema for the asset index structure
export const AssetIndexSchema = z.object({
  folders: z.record(z.string(), z.string()),
  files: z.record(
    z.string(),
    z.object({
      folder: z.string(),
      path: z.string(),
    }),
  ),
})

export type AssetIndex = z.infer<typeof AssetIndexSchema>

// Asset registry service for managing asset lookups (factory function pattern)
const createAssetRegistryService = () => {
  let assetIndex: AssetIndex | null = null
  let loadingPromise: Promise<AssetIndex> | null = null
  const CDN_BASE = 'https://cdn.nicelittlethings.be/design-studio/assets'
  const INDEX_URL = `${CDN_BASE}/index.json`

  // Load the asset index from CDN
  const loadIndex = async (): Promise<AssetIndex> => {
    try {
      // Loading asset index from CDN
      const response = await fetch(INDEX_URL)
      if (!response.ok) {
        const errorMsg = `Failed to load asset index: ${response.status} ${response.statusText}`
        Sentry.captureException(new Error(errorMsg), {
          tags: {
            component: 'assetRegistry',
            httpStatus: response.status,
          },
          extra: {
            url: INDEX_URL,
            status: response.status,
            statusText: response.statusText,
          },
        })
        throw new Error(errorMsg)
      }
      const data = await response.json()
      const validatedIndex = AssetIndexSchema.parse(data)
      assetIndex = validatedIndex

      // Index loaded successfully

      return validatedIndex
    } catch (error) {
      console.error('[AssetRegistry] Failed to load asset index:', error)

      Sentry.captureException(error as Error, {
        tags: {
          component: 'assetRegistry',
          function: 'loadIndex',
        },
        extra: {
          url: INDEX_URL,
          error: error instanceof Error ? error.message : String(error),
        },
      })

      throw new Error('Could not load asset registry')
    }
  }

  // Get the asset index, loading it if necessary
  const getIndex = async (): Promise<AssetIndex> => {
    if (assetIndex) {
      return assetIndex
    }

    // If already loading, wait for the existing promise
    if (loadingPromise) {
      return loadingPromise
    }

    // Start loading
    loadingPromise = loadIndex()
    try {
      const index = await loadingPromise
      return index
    } finally {
      loadingPromise = null
    }
  }

  // Get asset path from assetId
  const getAssetPath = async (assetId: string): Promise<string | null> => {
    const index = await getIndex()
    const asset = index.files[assetId]
    return asset?.path || null
  }

  // Get full CDN URL for an asset
  const getAssetUrl = async (assetId: string): Promise<string | null> => {
    const path = await getAssetPath(assetId)
    if (!path) return null
    return `${CDN_BASE}/${path}`
  }

  // Get asset type (print/engrave) from assetId
  const getAssetType = async (assetId: string): Promise<'print' | 'engrave' | null> => {
    const path = await getAssetPath(assetId)
    if (!path) return null

    if (path.startsWith('print/')) return 'print'
    if (path.startsWith('engrave/')) return 'engrave'
    return null
  }

  // Get folder name from assetId
  const getFolderName = async (assetId: string): Promise<string | null> => {
    const index = await getIndex()
    const asset = index.files[assetId]
    if (!asset) return null

    const folderPath = index.folders[asset.folder]
    if (!folderPath) return null

    // Extract folder name from path (e.g., "print/Boerderij" -> "Boerderij")
    const parts = folderPath.split('/')
    return parts[1] || null
  }

  // Get file name from assetId
  const getFileName = async (assetId: string): Promise<string | null> => {
    const path = await getAssetPath(assetId)
    if (!path) return null

    // Extract filename from path
    const parts = path.split('/')
    return parts[parts.length - 1] || null
  }

  // Helper method to get all asset info
  const getAssetInfo = async (assetId: string) => {
    const index = await getIndex()
    const asset = index.files[assetId]

    if (!asset) {
      console.warn('[AssetRegistry] Asset not found:', {
        assetId,
        availableAssets: Object.keys(index.files).slice(0, 10),
      })

      Sentry.captureMessage('Asset not found in registry', {
        level: 'warning',
        tags: {
          component: 'assetRegistry',
          function: 'getAssetInfo',
        },
        extra: {
          assetId,
          totalAssets: Object.keys(index.files).length,
        },
      })

      return null
    }

    const folderPath = index.folders[asset.folder]
    const pathParts = asset.path.split('/')
    const folderParts = folderPath?.split('/') || []

    const assetInfo = {
      assetId,
      path: asset.path,
      url: `${CDN_BASE}/${asset.path}`,
      type: pathParts[0] as 'print' | 'engrave',
      folderName: folderParts[1] || pathParts[1],
      fileName: pathParts[pathParts.length - 1],
      folderId: asset.folder,
    }

    // Asset info retrieved successfully
    return assetInfo
  }

  // Check if an assetId exists
  const hasAsset = async (assetId: string): Promise<boolean> => {
    const index = await getIndex()
    return assetId in index.files
  }

  // Clear the cache (useful for testing or refresh)
  const clearCache = (): void => {
    assetIndex = null
    loadingPromise = null
  }

  return {
    getIndex,
    getAssetPath,
    getAssetUrl,
    getAssetType,
    getFolderName,
    getFileName,
    getAssetInfo,
    hasAsset,
    clearCache,
  }
}

// Singleton instance
let assetRegistryInstance: ReturnType<typeof createAssetRegistryService> | null = null

export const assetRegistry = (() => {
  if (!assetRegistryInstance) {
    assetRegistryInstance = createAssetRegistryService()
  }
  return assetRegistryInstance
})()
